import { useEffect, useMemo, useState } from "react";
import {
  api,
  formatValue,
  type Dashboard,
  type DetailResult,
  type Filter,
  type QueryRow,
  type ValueFormat,
} from "../api";

/**
 * The pivot: choose a dimension and a metric, get a ranked breakdown, click a
 * row to drill into it.
 *
 * Everything offered here comes from config — the dimension list and the metric
 * list are sent by the API. Adding "break down by region" for a client is a line
 * in their config, not a change to this component.
 */
export function Pivot({
  dashboard,
  filters,
  onDrill,
}: {
  dashboard: Dashboard;
  filters: Filter[];
  onDrill: (filter: Filter) => void;
}) {
  const dimensions = dashboard.dimensions;
  const metrics = dashboard.metrics;

  // The dimension the USER picked. What is actually shown may differ while that
  // dimension is filtered out (see `dimension` below) — keeping the preference
  // separate is what lets the breakdown return to it once the filter is cleared.
  const [preferred, setPreferred] = useState(dimensions[0]?.field ?? "");
  const [metric, setMetric] = useState(metrics[0]?.name ?? "");
  const [rows, setRows] = useState<QueryRow[]>([]);
  const [mtdByLabel, setMtdByLabel] = useState<Record<string, number | null>>({});
  const [loading, setLoading] = useState(false);
  // Checked labels for the "filter to many at once" flow.
  const [checked, setChecked] = useState<Set<string>>(new Set());

  // Dimensions already filtered to a single value have nothing left to break
  // down, so drop them from the picker rather than offering a dead end.
  const available = useMemo(
    () => dimensions.filter((d) => !filters.some((f) => f.field === d.field)),
    [dimensions, filters],
  );

  // What to actually break down by: the user's choice when it is still available,
  // otherwise the first one that is. Derived rather than stored, so when a filter
  // is cleared the preferred dimension comes back on its own. Storing it (the
  // previous approach) made the switch a one-way door — drilling into a customer
  // moved the pivot to salesperson and clearing the filter left it there.
  const dimension = useMemo(() => {
    if (available.some((d) => d.field === preferred)) return preferred;
    return available[0]?.field ?? "";
  }, [available, preferred]);

  // Clearing the selection whenever the pivot changes avoids carrying a stale
  // set of customer names onto a salesperson breakdown.
  useEffect(() => setChecked(new Set()), [dimension, filters]);

  useEffect(() => {
    if (!dimension || !metric) return;
    setLoading(true);
    api
      .query({
        dataset: dashboard.dataset,
        filters,
        group_by: dimension,
        metrics: [metric, "invoice_count"].filter((m) =>
          metrics.some((x) => x.name === m),
        ),
        // Fetch the whole breakdown (a dimension rarely exceeds ~200 groups),
        // then rank client-side by the chosen metric. Ordering server-side would
        // sort by row count, which is not what "top customers by revenue" means.
        limit: 200,
      })
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [dashboard.dataset, dimension, metric, filters, metrics]);

  // A month-to-date value per group, shown as its own column. It keeps any
  // non-date filters (e.g. a salesperson) but always uses the MTD window, so an
  // "MTD" column means the same thing whatever the toolbar range is set to.
  const periodField = dashboard.period_field;
  useEffect(() => {
    if (!dimension || !metric || !periodField || !dashboard.mtd_from || !dashboard.mtd_to) {
      setMtdByLabel({});
      return;
    }
    const nonDate = filters.filter((f) => f.field !== periodField);
    api
      .query({
        dataset: dashboard.dataset,
        filters: [
          ...nonDate,
          { field: periodField, op: "gte", value: dashboard.mtd_from },
          { field: periodField, op: "lte", value: dashboard.mtd_to },
        ],
        group_by: dimension,
        metrics: [metric],
        limit: 200,
      })
      .then((r) =>
        setMtdByLabel(Object.fromEntries(r.map((x) => [x.group ?? "—", x.values[metric] ?? null]))),
      )
      .catch(() => setMtdByLabel({}));
  }, [dashboard.dataset, dashboard.mtd_from, dashboard.mtd_to, periodField, dimension, metric, filters]);

  const format: ValueFormat =
    metrics.find((m) => m.name === metric)?.format ?? "number";
  const dimLabel = dimensions.find((d) => d.field === dimension)?.label;

  // Which column the table is sorted by, and which way. Defaults to the chosen
  // metric, biggest first — click any header to change it.
  const [sortCol, setSortCol] = useState<"name" | "value" | "mtd" | "share" | "invoices">(
    "value",
  );
  const [descending, setDescending] = useState(true);
  // Show the top slice by default; expand to the full list on request.
  const [expanded, setExpanded] = useState(false);
  const TOP = 15;

  const sortValue = (r: QueryRow, col: typeof sortCol): number | string => {
    switch (col) {
      case "name":
        return r.group ?? "";
      case "mtd":
        return Math.abs(mtdByLabel[r.group ?? "—"] ?? 0);
      case "invoices":
        return r.row_count;
      // Share is proportional to the metric value (value / total), so it sorts
      // the same way — offered for completeness.
      default:
        return Math.abs(r.values[metric] ?? 0);
    }
  };

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sortCol);
      const bv = sortValue(b, sortCol);
      const cmp = typeof av === "string" ? av.localeCompare(bv as string) : av - (bv as number);
      return descending ? -cmp : cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, metric, sortCol, descending, mtdByLabel]);

  const ranked = expanded ? sorted : sorted.slice(0, TOP);

  const max = Math.max(...ranked.map((r) => Math.abs(r.values[metric] ?? 0)), 1);
  const total = rows.reduce((sum, r) => sum + (r.values[metric] ?? 0), 0);

  const sortBy = (col: typeof sortCol) => {
    if (col === sortCol) {
      setDescending((d) => !d);
    } else {
      setSortCol(col);
      setDescending(col !== "name"); // names A→Z, numbers biggest-first
    }
  };
  const arrow = (col: typeof sortCol) => (sortCol === col ? (descending ? " ▾" : " ▴") : "");

  const toggle = (label: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  const applySelected = () => {
    const values = [...checked];
    if (values.length === 0) return;
    // One value is an equality; several become a single 'in' filter — the
    // "filter by many customers at once" the client asked for.
    onDrill(
      values.length === 1
        ? { field: dimension, op: "eq", value: values[0], label: dimLabel }
        : { field: dimension, op: "in", value: values, label: dimLabel },
    );
    setChecked(new Set());
  };

  if (!dimensions.length) return null;

  const metricLabel = metrics.find((m) => m.name === metric)?.label ?? "value";

  return (
    <div className="panel">
      <div className="panel-header row">
        <div>
          <h2>Break down</h2>
          <p>
            {metricLabel} by {dimLabel?.toLowerCase() ?? "group"} — click rows to select,
            then filter the whole dashboard to them
          </p>
        </div>
        <div className="controls">
          <label className="control">
            <span>Break down by</span>
            <select value={dimension} onChange={(e) => setPreferred(e.target.value)}>
              {available.map((d) => (
                <option key={d.field} value={d.field}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <label className="control">
            <span>Showing</span>
            <select value={metric} onChange={(e) => setMetric(e.target.value)}>
              {metrics.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading && <div className="empty">Calculating…</div>}
      {!loading && rows.length === 0 && <div className="empty">Nothing matches</div>}

      {!loading && rows.length > 0 && (
        <>
          <div className={`pivot${expanded ? " scroll" : ""}`}>
            <div className={`pivot-head${periodField ? " with-mtd" : ""}`}>
              <span className="pv-check" />
              <span className="pv-name sortable" onClick={() => sortBy("name")}>
                {dimLabel}
                {arrow("name")}
              </span>
              <span className="pv-bar" />
              <span className="pv-num sortable" onClick={() => sortBy("value")}>
                {metricLabel}
                {arrow("value")}
              </span>
              {periodField && (
                <span className="pv-num sortable" onClick={() => sortBy("mtd")}>
                  MTD{arrow("mtd")}
                </span>
              )}
              <span className="pv-num sortable" onClick={() => sortBy("share")}>
                Share{arrow("share")}
              </span>
              <span className="pv-num sortable" onClick={() => sortBy("invoices")}>
                Invoices{arrow("invoices")}
              </span>
            </div>
            {ranked.map((r) => {
              const value = r.values[metric] ?? 0;
              const label = r.group ?? "—";
              const selected = checked.has(label);
              return (
                <div
                  className={`pivot-row${selected ? " selected" : ""}${periodField ? " with-mtd" : ""}`}
                  key={label}
                  onClick={() => toggle(label)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggle(label)}
                >
                  <span className="pv-check">{selected ? "✓" : ""}</span>
                  <span className="pv-name" title={label}>
                    {label}
                  </span>
                  <span className="pv-bar">
                    <span
                      className={`pv-bar-fill${value < 0 ? " negative" : ""}`}
                      style={{ width: `${(Math.abs(value) / max) * 100}%` }}
                    />
                  </span>
                  <span className="pv-num strong">{formatValue(value, format, "short")}</span>
                  {periodField && (
                    <span className="pv-num">
                      {formatValue(mtdByLabel[label] ?? null, format, "short")}
                    </span>
                  )}
                  <span className="pv-num muted">
                    {total !== 0 ? `${((value / total) * 100).toFixed(1)}%` : "—"}
                  </span>
                  <span className="pv-num muted">{r.row_count.toLocaleString()}</span>
                </div>
              );
            })}
          </div>

          {sorted.length > TOP && (
            <button className="ghost small-btn expand-btn" onClick={() => setExpanded((e) => !e)}>
              {expanded ? `Show top ${TOP}` : `Show all ${sorted.length}`}
            </button>
          )}

          {checked.size > 0 && (
            <div className="selection-bar">
              <span>
                {checked.size} {dimLabel?.toLowerCase() ?? "item"}
                {checked.size === 1 ? "" : "s"} selected
              </span>
              <button onClick={applySelected}>
                Filter to {checked.size === 1 ? "this" : "these"}
              </button>
              <button className="ghost small-btn" onClick={() => setChecked(new Set())}>
                Clear
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** The rows themselves — the bottom of any drill. */
export function DetailTable({
  dataset,
  filters,
  sortField,
}: {
  dataset: string;
  filters: Filter[];
  sortField: string | null;
}) {
  const [result, setResult] = useState<DetailResult | null>(null);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<string | null>(sortField);
  const [descending, setDescending] = useState(true);
  const [open, setOpen] = useState(false);
  const pageSize = 50;

  // Any change of filter invalidates the current page.
  useEffect(() => setOffset(0), [filters]);

  useEffect(() => {
    if (!open) return;
    api
      .detail({ dataset, filters, sort, descending, limit: pageSize, offset })
      .then(setResult)
      .catch(() => setResult(null));
  }, [dataset, filters, sort, descending, offset, open]);

  function toggleSort(column: string) {
    if (sort === column) setDescending((d) => !d);
    else {
      setSort(column);
      setDescending(true);
    }
    setOffset(0);
  }

  return (
    <div className="panel">
      <div className="panel-header row">
        <div>
          <h2>Rows</h2>
          <p>
            {result
              ? `${result.total.toLocaleString()} matching`
              : "The individual records behind these numbers"}
          </p>
        </div>
        <div className="controls">
          <button
            className="ghost"
            onClick={() => api.exportDetail({ dataset, filters, sort, descending })}
            title="Download every matching row as CSV"
          >
            Export CSV
          </button>
          <button className="ghost" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show rows"}
          </button>
        </div>
      </div>

      {open && result && (
        <>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  {result.columns.map((c) => (
                    <th
                      key={c}
                      className="sortable"
                      onClick={() => toggleSort(c)}
                      title="Sort by this column"
                    >
                      {c.replace(/_/g, " ")}
                      {sort === c ? (descending ? " ▾" : " ▴") : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, i) => (
                  <tr key={i}>
                    {result.columns.map((c) => (
                      <td key={c} className={isNumeric(row[c]) ? "numeric" : ""}>
                        {row[c] === null || row[c] === undefined ? "—" : String(row[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button
              className="ghost"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
            >
              Previous
            </button>
            <span className="muted small">
              {result.total === 0
                ? "0"
                : `${offset + 1}–${Math.min(offset + pageSize, result.total)} of ${result.total.toLocaleString()}`}
            </span>
            <button
              className="ghost"
              disabled={offset + pageSize >= result.total}
              onClick={() => setOffset((o) => o + pageSize)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function isNumeric(value: unknown): boolean {
  return typeof value === "string" && value !== "" && !Number.isNaN(Number(value));
}
